import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex items-end justify-between gap-4 border-gray-300 border-b pb-4 -mx-4 sm:-mx-6 px-4 sm:px-6 bg-white" }
const _hoisted_2 = { class: "block md:hidden" }
const _hoisted_3 = { class: "hidden md:block" }
const _hoisted_4 = {
  key: 0,
  class: "text-red-500"
}
const _hoisted_5 = { class: "flex gap-4 flex-none" }
const _hoisted_6 = { class: "flex flex-row -mr-6 min-h-full h-full" }
const _hoisted_7 = { class: "w-full md:flex-none md:w-96 pt-4 pb-[177px] pr-4 sm:pr-6 h-full min-h-content overflow-y-auto" }
const _hoisted_8 = { class: "flex justify-between items-center mb-4" }
const _hoisted_9 = { class: "section-title" }
const _hoisted_10 = { class: "flex items-center justify-end md:hidden" }
const _hoisted_11 = { class: "bg-gray-100 w-full hidden md:block p-4 sm:px-6 h-full min-h-content overflow-y-auto" }
const _hoisted_12 = { class: "section-title" }
const _hoisted_13 = { class: "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5 gap-6 mt-4 max-w-screen-2xl mx-auto pb-[177px]" }
const _hoisted_14 = { class: "flex items-center justify-end space-x-4" }
const _hoisted_15 = { class: "section-title" }
const _hoisted_16 = { class: "space-y-6 mt-4" }

import { 
		Auth
	} from '@/api/Auth'

	// Components
	import SceneCard from './SceneCard.vue'
	import AvailableSceneCard from './AvailableSceneCard.vue'

	// Drag n drop
	import { 
		Container, 
		Draggable 
	} from 'vue3-smooth-dnd'

	// Languages
	import { 
		useI18n
	} from 'vue-i18n'

	// Mutations
	import {
		submitScenes
	} from '@/modules/jobs/mutations'

	// Store
	import {
		useSceneEditorStore
	} from '@/store/sceneEditor'

	// Types
	import type {
		Notification
	} from '@/types'

	// Vue
	import {
		computed,
		ref
	} from 'vue'

	
	const __default__ = { name: 'SceneEditor' } // name the component

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  emits: ['closeModal'],
  setup(__props, { emit: __emit }) {

	// Api
	const {t} = useI18n()
	const emit = __emit
	const sceneEditorStore = useSceneEditorStore()

	const scenes = computed(() => {
		return sceneEditorStore.sceneEditor.scenes
	})

	const availableScenes = computed(() => {
		return sceneEditorStore.sceneEditor.availableScenes
	})

	const confirmationOpen = ref(false)
	const confirmationTitle = ref(t('sceneEditor.confirmationTitleCancel'))
	const confirmationDescription = ref(t('sceneEditor.confirmationDescriptionCancel'))
	const confirmationCommand = ref('cancel')

	/**
	 * Opens the confirmation modal and sets the modal text based on the selected command
	 * @function openConfirmation
	 * @param {string} command - The command being performed (save/cancel)
	 */
	function openConfirmation(command: string) {
		confirmationTitle.value = command === 'save' ? t('sceneEditor.confirmationTitleSave') : t('sceneEditor.confirmationTitleCancel')
		confirmationDescription.value = command === 'save' ? t('sceneEditor.confirmationDescriptionSave') : t('sceneEditor.confirmationDescriptionCancel')
		confirmationCommand.value = command
		confirmationOpen.value = true
	}

	function confirmCancel() {
		confirmationOpen.value = false
	}

	const scriptError = ref('')

	async function confirmSave() {	
		if(confirmationCommand.value === 'save') await saveScenes()
		else emit('closeModal', confirmationCommand.value)
	}

	/**
	 * Submits the scene edits to FM
	 * @function saveScenes
	 */
	async function saveScenes() {
		scriptError.value = ''

		try {
			const jobId = sceneEditorStore.sceneEditor.jobId
			const scenesArray = sceneEditorStore.sceneEditor.scenes
			const scenes = scenesArray?.map((scene, i) => { 
				return { 
					sceneId: scene['job__JOBS_SCENES__jobID::_kf_scenes_id'], 
					jobSceneId: scene['job__JOBS_SCENES__jobID::_kp_jobs_scenes_id'], 
					contactSceneId: scene['job__JOBS_SCENES__jobID::_kf_contacts_scenes_id'],
					sceneNum: i + 1,
					isMainScene: scene['job__JOBS_SCENES__jobID::main_scene']
				}
			})
			const res = await submitScenes(jobId, scenes)
			const scriptResult = JSON.parse(res?.response?.scriptResult)

			if(scriptResult.errorCode !== '0') {
				const error = scriptResult.errorMessage
				throw error
			}

			const notification: Notification = {
				title: t('placeholders.success'),
				subtitle: t('sceneEditor.scenesUpdated'),
				icon: 0
			}

			emit('closeModal', confirmationCommand.value, notification)
		}
		catch(e) {
			console.log(e)
			scriptError.value = String(e)
		}
		finally {
			confirmationOpen.value = false
		}
	}

	const sceneEditorOpen = ref<{open: boolean}>({open: false})

	// #region Drag and Drop

	/**
	 * @function getSceneIdByIndex
	 * @param sceneIndex - The dragged item's scene index
	 */
	function getSceneIdByIndex(sceneIndex: any) {
		const scene = availableScenes.value[sceneIndex]
		return {sceneIndex, scene}
	}

	/**
	 * @function shouldAnimateDrop -  Set dropping animation, called by the target container to which the dragged item will be droppped
	 */
	function shouldAnimateDrop() {
		return false
	}

	const draggingIndex = ref(null)

	function onDragStart({ isSource, payload }) {
		if(!isSource) return
		// payload holds the item being dragged, find the index and apply class
		draggingIndex.value = payload.sceneIndex
	}

	function onDragEnd() {
		// Remove the dragging class when drag ends
		draggingIndex.value = null
	}

	function onDropScene(dropResult: any) {
		const { removedIndex, addedIndex, payload, element } = dropResult

		if(payload?.scene?.recordId?.length) {
			const scene = {
				recordId: payload.scene.recordId,
				"job__job_scene__SCENES__sceneID::name_display": payload.scene['jobs__contacts_scenes__SCENES__sceneID::name_display'],
				"job__JOBS_SCENES__jobID::scene_number": addedIndex + 1,
				"job__JOBS_SCENES__jobID::main_scene": payload.scene['jobs__CONTACTS_SCENES__serviceID::main_scene'],
				"job__JOBS_SCENES__jobID::standard": payload.scene['jobs__CONTACTS_SCENES__serviceID::standard'],
				"job__JOBS_SCENES__jobID::_kf_jobs_id": payload.scene['JOBS::_kp_jobs_id'],
				"job__job_scene__job_song__SONGS__songID::name_display": '',
				"job__JOBS_SCENES__jobID::_kp_jobs_scenes_id": '',
				"job__JOBS_SCENES__jobID::_kf_contacts_scenes_id": payload.scene['jobs__CONTACTS_SCENES__serviceID::_kp_contacts_scenes_id'],
				"job__JOBS_SCENES__jobID::_kf_scenes_id": payload.scene['jobs__CONTACTS_SCENES__serviceID::_kf_scenes_id'],
				modId: payload.scene.modId
			}

			sceneEditorStore.sceneEditor.scenes.splice(addedIndex, 0, scene)
		}
		else {
			onDropCurrentScene(dropResult)
		}
	}

	/**
	 * Updates the scene order within the store
	 * @function onDropCurrentScene
	 * @param dropResult - The drop result event
	 */
	function onDropCurrentScene(dropResult: any) {
		const { removedIndex, addedIndex, payload, element } = dropResult

		// Validate index
		if (removedIndex < 0 || removedIndex >= scenes.value.length || addedIndex < 0 || addedIndex >= scenes.value.length) return

		// Update scene order within in store
		const sceneToMove = scenes.value.splice(removedIndex, 1)[0]
		sceneEditorStore.sceneEditor.scenes.splice(addedIndex, 0, sceneToMove)
	}

	// #endregion


return (_ctx: any,_cache: any) => {
  const _component_tw_button = _resolveComponent("tw-button")!
  const _component_v_dialog_title = _resolveComponent("v-dialog-title")!
  const _component_v_dialog_description = _resolveComponent("v-dialog-description")!
  const _component_v_dialog_body = _resolveComponent("v-dialog-body")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_tw_modal = _resolveComponent("tw-modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(t)('sceneEditor.modalDescriptionMobile')), 1),
      _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)('sceneEditor.modalDescription')), 1),
      (scriptError.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_unref(t)('sceneEditor.generalError')), 1))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_component_tw_button, {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (openConfirmation('cancel'))),
          class: "w-full whitespace-nowrap"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('sceneEditor.cancel')), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_tw_button, {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (openConfirmation('save'))),
          class: "w-full btn-brand"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('sceneEditor.save')), 1)
          ]),
          _: 1
        })
      ])
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("h2", _hoisted_9, _toDisplayString(_unref(t)('sceneEditor.sidebarTitle')), 1),
          _createElementVNode("div", _hoisted_10, [
            _createVNode(_component_tw_button, {
              onClick: _cache[2] || (_cache[2] = () => {sceneEditorOpen.value.open = true}),
              class: "w-full btn-brand"
            }, {
              default: _withCtx(() => [
                _createTextVNode(" + " + _toDisplayString(_unref(t)('sceneEditor.addScene')), 1)
              ]),
              _: 1
            })
          ])
        ]),
        _createVNode(_unref(Container), {
          "group-name": "col",
          "drag-class": "card-ghost",
          "drop-class": "card-ghost-drop",
          "drag-handle-selector": ".draggable-card",
          onDrop: _cache[3] || (_cache[3] = ($event: any) => (onDropScene($event))),
          class: "space-y-6",
          "should-accept-drop": () => {return true}
        }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scenes.value, (scene, i) => {
              return (_openBlock(), _createBlock(SceneCard, {
                key: scene?.recordId,
                i: i,
                scene: scene
              }, null, 8, ["i", "scene"]))
            }), 128))
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("h2", _hoisted_12, _toDisplayString(_unref(t)('sceneEditor.availableScenes')), 1),
        _createElementVNode("div", _hoisted_13, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableScenes.value, (scene, sceneIndex) => {
            return (_openBlock(), _createBlock(_unref(Container), {
              key: sceneIndex,
              tag: "span",
              "group-name": "results",
              behaviour: "copy",
              "drag-class": "card-ghost",
              "drop-class": "card-ghost-drop",
              "get-child-payload": () => getSceneIdByIndex(sceneIndex),
              "should-animate-drop": shouldAnimateDrop,
              onOnDragStart: onDragStart,
              onOnDragEnd: onDragEnd
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Draggable), null, {
                  default: _withCtx(() => [
                    _createVNode(AvailableSceneCard, {
                      class: _normalizeClass(['draggable-item  mx-auto', { 'opacity-50': draggingIndex.value === sceneIndex }]),
                      scene: scene
                    }, null, 8, ["class", "scene"])
                  ]),
                  _: 2
                }, 1024)
              ]),
              _: 2
            }, 1032, ["get-child-payload"]))
          }), 128))
        ])
      ])
    ]),
    _createVNode(_component_v_dialog, {
      "block-overlay-close": "",
      open: confirmationOpen.value,
      size: "md"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_dialog_title, null, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(confirmationTitle.value), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_v_dialog_description, null, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(confirmationDescription.value), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_v_dialog_body, null, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_14, [
              _createVNode(_component_tw_button, {
                onClick: confirmCancel,
                class: "w-full"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('sceneEditor.cancel')), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_tw_button, {
                onClick: confirmSave,
                class: "w-full btn-brand"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('sceneEditor.confirm')), 1)
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(_component_tw_modal, {
      "modal-max-width": "max-w-full",
      "modal-min-height": "h-screen",
      ref_key: "sceneEditorOpen",
      ref: sceneEditorOpen,
      title: _unref(t)('sceneEditor.modalTitle')
    }, {
      default: _withCtx(() => [
        _createElementVNode("h2", _hoisted_15, _toDisplayString(_unref(t)('sceneEditor.availableScenes')), 1),
        _createElementVNode("div", _hoisted_16, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableScenes.value, (scene) => {
            return (_openBlock(), _createBlock(AvailableSceneCard, {
              key: scene?.recordId,
              onCloseModal: _cache[4] || (_cache[4] = () => {sceneEditorOpen.value.open = false}),
              scene: scene
            }, null, 8, ["scene"]))
          }), 128))
        ])
      ]),
      _: 1
    }, 8, ["title"])
  ], 64))
}
}

})